(ns app.front.view.group.admin.templates.subs
  (:require [ajax.core :refer [POST]]
            [app.domain.util :as du]
            [app.front.entities :as entities]
            [app.front.file.upload :as upload]
            [app.front.view.group.admin.subs :as gas]
            [cljs.reader :as reader]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [core.front.event :refer [<sub >evt]]
            [core.front.href :as href]
            [core.util.util :as util]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]))

(defn template-href [group-id template]
  (href/href :view/group.admin.template
             (util/mmap {:template-id (or (-> template :template/replacement :db/id)
                                          (:db/id template))}
                        group-id)))

(rf/reg-event-fx ::fetch-fragments.success
  (fn [{:keys [db]} [_ fragment-id status response]]
    (when (= 200 status)
      {:db (assoc-in db [::fragment-contents fragment-id] (:content response))})))

(defn resolve-fragments
  [group-id response]
  (->> response
       vals
       (filter (eu/is-type-fn :entity.type/document-fragment))
       (mapv (fn [{:db/keys [id]}]
               [:comm/command {:command    :document/download-fragment
                               :method     :get
                               :data       {:group-id    group-id
                                            :fragment-id id}
                               :on-success [::fetch-fragments.success id]}]))))

(rf/reg-event-fx ::fetch-template.success
  (fn [_ [_ group-id status response]]
    {:fx (into [[:dispatch [::entities/merge-entity-db status response]]]
               (resolve-fragments group-id response))}))

(rf/reg-event-fx ::fetch-templates.success
  (fn [_ [_ status response]]
    {:dispatch [::entities/merge-entity-db status response]}))

(rf/reg-event-fx ::fetch-templates
  (fn [_ [_ group-id]]
    {:comm/command {:command    :group/templates
                    :method     :get
                    :data       {:group-id group-id}
                    :on-success [::fetch-templates.success]}}))

(rf/reg-event-fx ::fetch-system-email-templates.success
  (fn [{:keys [db]} [_ group-id min-version status response]]
    (if (= 200 status)
      (let [response-version (or (some-> response first :entity/version) min-version 0)
            stored-version   (get-in db [::system-email-templates-version group-id] 0)]
        {:db (cond-> (update db ::system-email-templates-loading disj group-id)
               (<= stored-version response-version)
               (-> (assoc-in [::system-email-templates group-id] response)
                   (assoc-in [::system-email-templates-version group-id] response-version)))})
      {:db (update db ::system-email-templates-loading disj group-id)})))

(rf/reg-event-fx ::fetch-system-email-templates
  (fn [{:keys [db]} [_ group-id min-version]]
    {:db           (update db ::system-email-templates-loading (fnil conj #{}) group-id)
     :comm/command {:command    :group/system-email-templates
                    :method     :get
                    :data       (p/assoc-if {:group-id group-id}
                                            :min-version min-version)
                    :on-success [::fetch-system-email-templates.success group-id min-version]}}))

(defn- route-group [db group-id]
  (or (entities/find-entity (entities/db->context db) group-id)
      (p/find-first (concat (-> db :session/user :user/authority-groups)
                            (-> db :session/user :user/administered-groups)
                            (-> db :session/user :user/domain-groups)
                            (-> db :session/user :user/superdomain-groups))
                    {:db/id group-id})))

(rf/reg-event-fx ::fetch-system-email-templates-when-needed
  (fn [{:keys [db]} [_ group-id tab]]
    ;; Do not gate this fetch on feature flags. They load asynchronously, and waiting for
    ;; them here can leave the route mounted without the data needed once the view appears.
    (let [group         (route-group db group-id)
          email-tab?    (= "system-emails" tab)
          domain-group? (= :group.type/domain (:group/type group))
          loaded?       (contains? (::system-email-templates db) group-id)
          loading?      (contains? (::system-email-templates-loading db) group-id)]
      (when (and (or email-tab? domain-group?)
                 (not loaded?)
                 (not loading?))
        {:dispatch [::fetch-system-email-templates group-id]}))))

(rf/reg-sub ::system-email-template-view-data
  :<- [:route/match]
  (fn [match]
    {:back-href (href/href :view/group.admin.templates
                           {:group-id (-> match :parameters :path :group-id)}
                           {:tab "system-emails"})}))

(rf/reg-sub ::system-email-templates
  (fn [db [_ group-id]]
    (get-in db [::system-email-templates group-id] [])))

(defn- non-blank-vals [m]
  (p/remove-vals str/blank? m))

(defn- system-email-template-save-values [draft]
  {:inherit-from-organization (boolean (:inherit-from-organization draft))
   :subjects                  (non-blank-vals (:subjects draft))
   :bodies                    (non-blank-vals (:bodies draft))})

(defn- system-email-template-draft [{:keys [custom-subjects inherited-subjects
                                            custom-bodies inherited-bodies
                                            inherit-from-organization?]}]
  {:subjects                  (if inherit-from-organization?
                                (cond
                                  (seq custom-subjects) custom-subjects
                                  (seq inherited-subjects) inherited-subjects
                                  :else {})
                                (or custom-subjects {}))
   :bodies                    (if inherit-from-organization?
                                (cond
                                  (seq custom-bodies) custom-bodies
                                  (seq inherited-bodies) inherited-bodies
                                  :else {})
                                (or custom-bodies {}))
   :inherit-from-organization (boolean inherit-from-organization?)})

(defn- changed-entity-version [response entity-id]
  (some-> response
          (get entity-id)
          :entity/version
          second))

(defn- change-value [changes attr op]
  (let [ops (get changes attr)
        ops (cond
              (nil? ops) nil
              (keyword? (first ops)) [ops]
              :else ops)]
    (some (fn [[change-op value]]
            (when (= op change-op)
              value))
          ops)))

(defn- changed-body-fragments [response]
  (into {}
        (keep (fn [[_ changes]]
                (when-let [lang (change-value changes :entity/language :assoc)]
                  (when-let [fragment-id (change-value changes :document/fragments :conj)]
                    [lang fragment-id]))))
        response))

(defn- system-email-template-upload-data [group-id body-fragments response bodies]
  (let [body-fragments (merge body-fragments (changed-body-fragments response))]
    (->> (non-blank-vals bodies)
         (keep (fn [[lang content]]
                 (when-let [fragment-id (get body-fragments lang)]
                   (util/mmap group-id fragment-id content))))
         distinct)))

(defn- upload-bodies [save-data]
  (when-not (:inherit-from-organization save-data)
    (:bodies save-data)))

(defn- string-map-with-keyword-keys? [m]
  (and (map? m)
       (every? (fn [[k v]]
                 (and (keyword? k)
                      (string? v)))
               m)))

(defn- valid-system-email-template-payload? [payload]
  (and (map? payload)
       (keyword? (:email-type payload))
       (boolean? (:inherit-from-organization payload))
       (string-map-with-keyword-keys? (:subjects payload))
       (string-map-with-keyword-keys? (:bodies payload))))

(rf/reg-event-fx ::refresh-system-email-template
  (fn [_ [_ group-id email-type min-version]]
    {:fx [[:dispatch [::fetch-system-email-templates group-id min-version]]
          [:dispatch [::fetch-system-email-template group-id email-type min-version]]]}))

(rf/reg-event-fx ::revert-system-email-template
  (fn [{:keys [db]} [_ group-id email-type]]
    {:dispatch [::perform-system-email-template-save
                group-id
                email-type
                :group/revert-system-email-template
                {:group-id   group-id
                 :email-type email-type}
                (get-in db [::system-email-template group-id email-type :body-fragments])
                {:success :system-email-template/reverted}]}))

(rf/reg-event-fx ::fetch-system-email-template.success
  (fn [{:keys [db]} [_ group-id status {:keys [email-type] :as template}]]
    (when (and (= 200 status)
               (<= (get-in db [::system-email-template group-id email-type :entity/version] 0)
                   (:entity/version template 0)))
      ;; Bump editor-version so the rich-text editor's `key` changes and it remounts with the
      ;; freshly fetched body - it only reads `initial-value` on mount, not on every re-render.
      (let [prev-editor-version (get-in db [::system-email-template-draft group-id email-type :editor-version] 0)]
        {:db (-> db
                 (assoc-in [::system-email-template group-id email-type] template)
                 (assoc-in [::system-email-template-draft group-id email-type]
                           (assoc (system-email-template-draft template)
                                  :editor-version (inc prev-editor-version))))}))))

(rf/reg-event-fx ::fetch-system-email-template
  (fn [_ [_ group-id email-type min-version]]
    {:comm/command {:command    :group/system-email-template
                    :method     :get
                    :data       (p/assoc-if (util/mmap group-id email-type)
                                            :min-version min-version)
                    :on-success [::fetch-system-email-template.success group-id]}}))

(rf/reg-event-fx ::fetch-system-email-template-for-route
  (fn [_ [_ group-id email-type]]
    ;; See ::fetch-system-email-templates-when-needed. The view still controls feature-flag visibility.
    {:dispatch [::fetch-system-email-template group-id email-type]}))

(rf/reg-sub ::system-email-template
  (fn [db [_ group-id email-type]]
    (get-in db [::system-email-template group-id email-type])))

(rf/reg-sub ::system-email-template-draft
  (fn [db [_ group-id email-type]]
    (-> db
        (get-in [::system-email-template-draft group-id email-type])
        (update :editor-version (fnil identity 0)))))

(rf/reg-sub ::system-email-template-dirty?
  (fn [db [_ group-id email-type]]
    (let [template (get-in db [::system-email-template group-id email-type])
          draft    (get-in db [::system-email-template-draft group-id email-type])]
      (and template
           draft
           (not= (system-email-template-save-values draft)
                 (system-email-template-save-values (system-email-template-draft template)))))))

(rf/reg-event-db ::set-system-email-template-subject
  [(rf/path ::system-email-template-draft)]
  (fn [db [_ group-id email-type lang value]]
    (assoc-in db [group-id email-type :subjects lang] value)))

(rf/reg-event-db ::set-system-email-template-body
  [(rf/path ::system-email-template-draft)]
  (fn [db [_ group-id email-type lang value]]
    (assoc-in db [group-id email-type :bodies lang] value)))

(rf/reg-event-db ::set-system-email-template-inheritance
  [(rf/path ::system-email-template-draft)]
  (fn [db [_ group-id email-type inherit?]]
    (-> db
        (assoc-in [group-id email-type :inherit-from-organization] inherit?)
        (update-in [group-id email-type :editor-version] inc))))

(rf/reg-event-db ::reset-system-email-template-draft
  (fn [db [_ group-id email-type]]
    (if-let [{:keys [custom-subjects inherited-subjects
                     custom-bodies inherited-bodies
                     inherit-from-organization?]} (get-in db [::system-email-template group-id email-type])]
      (-> db
          (assoc-in [::system-email-template-draft group-id email-type]
                    (system-email-template-draft (util/mmap custom-subjects
                                                            inherited-subjects
                                                            custom-bodies
                                                            inherited-bodies
                                                            inherit-from-organization?)))
          (update-in [::system-email-template-draft group-id email-type :editor-version] inc))
      db)))

(defn- system-email-save-data [group-id email-type draft]
  (assoc (system-email-template-save-values draft)
         :group-id group-id
         :email-type email-type))

(defn- system-email-template-key [group-id email-type]
  [group-id email-type])

(defn- upload-body-command [on-success {:keys [fragment-id] :as data}]
  [:comm/command {:command    :document/upload-fragment
                  :method     :post
                  :notify     nil
                  :on-success (conj on-success fragment-id)
                  :data       data}])

(defn- upload-body-commands [on-success upload-data]
  (mapv (partial upload-body-command on-success) upload-data))

(defn- refresh-system-email-template-command [group-id email-type response]
  [:dispatch [::refresh-system-email-template group-id email-type (changed-entity-version response group-id)]])

(rf/reg-event-fx ::finish-system-email-template-save
  (fn [{:keys [db]} [_ group-id email-type response]]
    (let [key       (system-email-template-key group-id email-type)
          remaining (dec (get-in db [::system-email-template-pending-uploads key] 1))]
      (if (pos? remaining)
        {:db (assoc-in db [::system-email-template-pending-uploads key] remaining)}
        {:db (p/dissoc-in db [::system-email-template-pending-uploads key])
         :fx [(refresh-system-email-template-command group-id email-type response)]}))))

(rf/reg-event-fx ::upload-system-email-template-body.success
  (fn [_ [_ group-id email-type save-response fragment-id status {:keys [changes] :as response}]]
    {:fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch [::fetch-fragments.success fragment-id status response]]
          [:dispatch [::finish-system-email-template-save group-id email-type save-response]]]}))

(rf/reg-event-fx ::save-system-email-template.success
  (fn [{:keys [db]} [_ group-id email-type body-fragments bodies status response]]
    (let [upload-data (system-email-template-upload-data group-id body-fragments response bodies)
          key         (system-email-template-key group-id email-type)]
      (cond-> {:fx [[:dispatch [::entities/apply-changes status response]]]}
        (seq upload-data)
        (-> (assoc :db (assoc-in db [::system-email-template-pending-uploads key] (count upload-data)))
            (update :fx into (upload-body-commands [::upload-system-email-template-body.success
                                                    group-id
                                                    email-type
                                                    response]
                                                   upload-data)))

        (empty? upload-data)
        (update :fx conj (refresh-system-email-template-command group-id email-type response))))))

(rf/reg-event-fx ::perform-system-email-template-save
  (fn [_ [_ group-id email-type command save-data body-fragments notify]]
    {:comm/command {:command    command
                    :data       save-data
                    :on-success [::save-system-email-template.success
                                 group-id
                                 email-type
                                 body-fragments
                                 (upload-bodies save-data)]
                    :notify     notify}}))

(rf/reg-event-fx ::save-system-email-template
  (fn [{:keys [db]} [_ group-id email-type]]
    (let [template       (get-in db [::system-email-template group-id email-type])
          body-fragments (:body-fragments template)
          draft          (get-in db [::system-email-template-draft group-id email-type])
          save-data      (system-email-save-data group-id email-type draft)]
      {:dispatch [::perform-system-email-template-save
                  group-id
                  email-type
                  :group/save-system-email-template
                  save-data
                  body-fragments
                  {:success :system-email-template/saved}]})))

(rf/reg-event-fx ::stop-tracking-system-email-template-file
  (fn [_ [_ component-id file-hash]]
    {:fx [[:dispatch [::upload/remove-pending-file component-id :placeholder]]
          [:dispatch [::upload/remove-pending-file component-id file-hash]]]}))

(rf/reg-event-fx ::import-system-email-template.success
  (fn [{:keys [db]} [_ group-id email-type body-fragments component-id file-hash response]]
    (let [changes     (:changes response)
          upload-data (system-email-template-upload-data group-id body-fragments changes (upload-bodies response))
          key         (system-email-template-key group-id email-type)]
      (cond-> {:fx [[:dispatch [::entities/apply-changes 200 changes]]
                    [:dispatch [::stop-tracking-system-email-template-file component-id file-hash]]
                    [:dispatch [:notifications/add :toast :success :success :system-email-template/imported]]]}
        (seq upload-data)
        (-> (assoc :db (assoc-in db [::system-email-template-pending-uploads key] (count upload-data)))
            (update :fx into (upload-body-commands [::upload-system-email-template-body.success
                                                    group-id
                                                    email-type
                                                    changes]
                                                   upload-data)))

        (empty? upload-data)
        (update :fx conj (refresh-system-email-template-command group-id email-type changes))))))

(defn- import-system-email-template! [group-id email-type body-fragments component-id file-hash content]
  (POST (str "/api/command/group/import-system-email-template?group-id="
             group-id
             "&email-type="
             email-type)
    {:headers       {"Content-Type" "application/edn"}
     :body          content
     :error-handler (fn [{:keys [status response]}]
                      (>evt [::stop-tracking-system-email-template-file component-id file-hash])
                      (>evt [::upload/upload-failure (util/mmap {:md5 file-hash} component-id) status response]))
     :handler       (fn [response]
                      (>evt [::import-system-email-template.success
                             group-id
                             email-type
                             body-fragments
                             component-id
                             file-hash
                             response]))}))

(rf/reg-event-fx ::perform-system-email-template-import
  (fn [{:keys [db]} [_ group-id email-type component-id file-hash content]]
    (import-system-email-template! group-id
                                   email-type
                                   (get-in db [::system-email-template group-id email-type :body-fragments])
                                   component-id
                                   file-hash
                                   content)
    {}))

(rf/reg-event-fx ::prepare-system-email-template-import
  (fn [_ [_ group-id email-type component-id file-hash content]]
    (try
      (let [payload (reader/read-string content)]
        (if (or (not (valid-system-email-template-payload? payload))
                (not= email-type (:email-type payload)))
          {:fx [[:dispatch [::stop-tracking-system-email-template-file component-id file-hash]]
                [:dispatch [:notifications/add :toast :error :error/error :system-email-template/import-wrong-type]]]}
          {:dispatch [::perform-system-email-template-import group-id email-type component-id file-hash content]}))
      (catch :default _
        {:fx [[:dispatch [::stop-tracking-system-email-template-file component-id file-hash]]
              [:dispatch [::upload/upload-failure (util/mmap {:md5 file-hash} component-id) 400 nil]]]}))))

(defn import-system-email-template-file [group-id email-type {:keys [file component-id file-hash]}]
  (>evt [::upload/add-pending-file component-id file-hash])
  (let [reader (js/FileReader.)]
    (set! (.-onload reader)
          #(>evt [::prepare-system-email-template-import
                  group-id
                  email-type
                  component-id
                  file-hash
                  (-> % .-target .-result)]))
    (set! (.-onerror reader)
          #(do
             (>evt [::stop-tracking-system-email-template-file component-id file-hash])
             (>evt [::upload/upload-failure (util/mmap {:md5 file-hash} component-id) 400 nil])))
    (.readAsText reader file)))

(rf/reg-event-db ::close-system-email-template-preview
  (fn [db _]
    (dissoc db ::system-email-template-preview)))

(defn- normalize-preview-response [response]
  (let [response (cond
                   (map? response) response
                   (object? response) (js->clj response :keywordize-keys true)
                   :else response)
        response (if (map? (:body response))
                   (:body response)
                   response)]
    (if (map? response)
      {:subject (or (:subject response) (get response "subject") "")
       :body    (or (:body response) (get response "body") "")}
      {:subject ""
       :body    (or response "")})))

(rf/reg-event-fx ::preview-system-email-template
  (fn [{:keys [db]} [_ group-id email-type]]
    (let [draft (get-in db [::system-email-template-draft group-id email-type])]
      {:comm/command {:command    :group/preview-system-email-template
                      :data       (system-email-save-data group-id email-type draft)
                      :on-success [::preview-system-email-template.success]}})))

(rf/reg-event-db ::preview-system-email-template.success
  (fn [db [_ status response]]
    (cond-> db
      (= 200 status) (assoc ::system-email-template-preview (normalize-preview-response response)))))

(rf/reg-sub ::system-email-template-preview
  :-> ::system-email-template-preview)

(rf/reg-event-fx ::fetch-template
  (fn [_ [_ group-id template-id]]
    {:comm/command {:command    :group/template
                    :method     :get
                    :data       (util/mmap group-id template-id)
                    :on-success [::fetch-template.success group-id]}}))

(defn templates-subscription [[_ group-id superdomain-id {:keys [entity-type permit-types]} _ _]]
  [(rf/subscribe [::gas/superdomain-group?])
   (rf/subscribe [::gas/superdomain])
   (rf/subscribe [::entities/entities {:entity/type            entity-type
                                       :domain/authority-group group-id}])
   (rf/subscribe [::entities/entities (let [pt-set (set permit-types)]
                                        (filter (fn [entity] (and (eu/is-type? entity-type entity)
                                                                  (= (:domain/authority-group entity) superdomain-id)
                                                                  (or (nil? (:template/permit-type entity))
                                                                      (empty? permit-types)
                                                                      (contains? pt-set (:template/permit-type entity)))))))])])

(defn update-template-names
  [[superdomain-group? superdomain templates inherited-templates] [_ _group-id _superdomain-id _entity-type tr name-kw]]
  (if name-kw
    (->> (if superdomain-group?
           (map (fn [template]
                  (if-let [pt (:template/permit-type template)]
                    (update (eu/->map template) name-kw str " (" (tr [pt]) ")")
                    template))
                templates)
           (concat
             templates
             (map #(update (eu/->map %) name-kw str " (" (:group/name superdomain) ")") inherited-templates)))
         (sort-by name-kw))
    (concat templates inherited-templates)))

(rf/reg-sub ::templates
  templates-subscription
  update-template-names)

(rf/reg-sub ::fee-templates
  (fn [[_ group-id]]
    [(rf/subscribe [::entities/entities {:entity/type            :entity.type/receipt-template
                                         :domain/authority-group group-id}])
     (rf/subscribe [::entities/entities {:entity/type            :entity.type/bill-pdf-template
                                         :domain/authority-group group-id}])])
  (fn [[inline-templates bill-templates] _]
    (concat inline-templates bill-templates)))

(rf/reg-sub ::template
  (fn [_ [_ group-id entity-type]]
    (<sub [::entities/entity {:entity/type            entity-type
                              :domain/authority-group group-id}])))

(rf/reg-sub ::template-by-subtype
  (fn [[_ group-id _]]
    (rf/subscribe [::entities/entities {:entity/type            :entity.type/receipt-template
                                        :domain/authority-group group-id}]))
  (fn [templates [_ _ subtype]]
    (p/find-first templates {:receipt-template/subtype subtype})))

(rf/reg-sub ::document-fragments
  :<- [::entities/entities {:entity/type :entity.type/document-fragment}]
  :-> du/entities->entity-db)

(rf/reg-sub ::fragment-ids-for-document
  (fn [[_ document-id _]]
    {:pre [(integer? document-id)]}
    (rf/subscribe [::entities/entity {:db/id document-id}]))
  (fn [document]
    (->> document
         :document/fragments
         (map :db/id)
         set)))

(rf/reg-sub ::fragment-id
  (fn [[_ document-id _]]
    {:pre [(integer? document-id)]}
    [(rf/subscribe [::document-fragments])
     (rf/subscribe [::fragment-ids-for-document document-id])])
  (fn [[fragments children] [_ _ fragment-type]]
    (-> fragments
        (select-keys children)
        vals
        (p/find-first {:document-fragment/type fragment-type})
        :db/id)))

(rf/reg-sub ::fragment-contents
  (fn [db]
    (::fragment-contents db {})))

(rf/reg-sub ::fragment-data-for
  (fn [[_ document-id fragment-type]]
    [(rf/subscribe [::fragment-id document-id fragment-type])
     (rf/subscribe [::fragment-contents])])
  (fn [[fragment-id fragments] _]
    {:id      fragment-id
     :content (get fragments fragment-id)}))

(rf/reg-event-fx ::remove-letter-template
  (fn [_ [_ template-id]]
    {:comm/command {:command    :letter/remove-template
                    :data       (util/mmap template-id)
                    :on-success [::entities/apply-changes]
                    :notify     {:success :template-editor/removed}}}))

(rf/reg-event-fx ::copy-letter-template
  (fn [_ [_ group-id template-id]]
    {:comm/command {:command    :letter/copy-template
                    :data       (util/mmap group-id template-id)
                    :on-success [::copy-letter-template.success group-id]
                    :notify     {:success :template-editor/template-copied}}}))

(rf/reg-event-fx ::copy-letter-template.success
  (fn [_ [_ group-id status db-changes]]
    (merge {:dispatch [::entities/apply-changes status db-changes]}
           (when-let [template-id (entities/id-of-some-in-apply-changes :entity.type/letter-pdf-template db-changes)]
             {:route/push {:route  :view/group.admin.template
                           :params {:group-id    group-id
                                    :template-id template-id}}}))))

(rf/reg-event-fx ::remove-package-template
  (fn [_ [_ template-id]]
    {:comm/command {:command    :package/remove-template
                    :data       (util/mmap template-id)
                    :on-success [::entities/apply-changes]
                    :notify     {:success :template-editor/removed}}}))

(rf/reg-event-fx ::delete-meeting-agenda-template
  (fn [_ [_ group-id template-id]]
    {:comm/command {:command    :committee/delete-meeting-agenda-template
                    :data       {:template-id template-id}
                    :on-success [::delete-meeting-agenda-template.success group-id]
                    :notify     {:success :template-editor/removed}}}))

(rf/reg-event-fx ::delete-meeting-agenda-template.success
  (fn [_ [_ group-id _status _changes]]
    {:fx [[:dispatch [::fetch-templates group-id]]]}))

(rf/reg-event-fx ::copy-meeting-agenda-template
  (fn [_ [_ group-id template]]
    {:comm/command {:command    :committee/create-meeting-agenda-template
                    :data       (cond-> {:authority-group-id           group-id
                                         :meeting-agenda-template/name (str (:meeting-agenda-template/name template) " (copy)")}
                                  (:committee-template/agenda-pdf-html-template template)
                                  (assoc :committee-template/agenda-pdf-html-template (:committee-template/agenda-pdf-html-template template))

                                  (:committee-template/agenda-pdf-css-template template)
                                  (assoc :committee-template/agenda-pdf-css-template (:committee-template/agenda-pdf-css-template template))

                                  (:committee-template/agenda-pdf-item-html-snippet template)
                                  (assoc :committee-template/agenda-pdf-item-html-snippet (:committee-template/agenda-pdf-item-html-snippet template))

                                  (:committee-template/agenda-pdf-deficiency-html-snippet template)
                                  (assoc :committee-template/agenda-pdf-deficiency-html-snippet (:committee-template/agenda-pdf-deficiency-html-snippet template)))
                    :on-success [::copy-meeting-agenda-template.success group-id]
                    :notify     {:success :template-editor/template-copied}}}))

(rf/reg-event-fx ::copy-meeting-agenda-template.success
  (fn [_ [_ group-id _status _changes]]
    {:fx [[:dispatch [::fetch-templates group-id]]]}))

(rf/reg-sub ::meeting-agenda-templates
  (fn [[_ group-id]]
    (rf/subscribe [::entities/entities {:domain/authority-group group-id
                                        :entity/type            :entity.type/meeting-agenda-template}]))
  (fn [templates [_ group-id]]
    (->> templates
         (filter #(du/in-state? :meeting-agenda-template.state/active %))
         (map (fn [template]
                {:id        (:db/id template)
                 :text      (:meeting-agenda-template/name template)
                 :link      (template-href group-id template)
                 :copy-fn   #(rf/dispatch [::copy-meeting-agenda-template group-id template])
                 :remove-fn #(rf/dispatch [::delete-meeting-agenda-template group-id (:db/id template)])}))
         (sort-by :text))))

;; fragment events
(defn do-save [{:keys [fragment-id notify?]
                :or   {notify? true}
                :as   data}]
  [:comm/command {:command    :document/upload-fragment
                  :method     :post
                  :notify     (when notify? {:success :template-editor/template-saved})
                  :on-success [::save-success fragment-id]
                  :data       (dissoc data :notify?)}])

(rf/reg-event-fx ::save-fragment
  (fn [_ [_ group-id fragment-id content]]
    {:fx [(do-save (util/mmap content group-id fragment-id))]}))

(rf/reg-event-fx ::save-success
  (fn [_ [_ fragment-id status {:keys [changes] :as response}]]
    {:fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch [::fetch-fragments.success fragment-id status response]]]}))

(rf/reg-event-fx ::save-fragments
  (fn [_ [_ group-id html-id css-id html css]]
    {:fx [(do-save (util/mmap {:content     html
                               :fragment-id html-id}
                              group-id))
          (do-save (util/mmap {:content     css
                               :fragment-id css-id
                               ;; Only show a single success notification
                               :notify?     false}
                              group-id))]}))


(rf/reg-event-fx ::create-fragment
  (fn [_ [_ group-id document-id fragment-type content]]
    {:fx [[:comm/command {:command    :document/create-fragment
                          :method     :post
                          :notify     {:success :template-editor/template-saved}
                          :on-success [::create-fragment-success]
                          :data       (util/mmap group-id
                                                 document-id
                                                 fragment-type
                                                 content)}]]}))


(rf/reg-event-fx ::create-fragment-success
  (fn [_ [_ status {:keys [changes fragment-id] :as response}]]
    {:fx [[:dispatch [::entities/apply-changes status changes]]
          [:dispatch [::fetch-fragments.success fragment-id status response]]]}))
